import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  DollarSign, Target, CheckCircle, Clock, AlertCircle, 
  ChevronDown, ChevronUp, Loader2, TrendingUp, Calendar,
  FileText, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GrantsPage() {
  const { t } = useLanguage();
  const [grants, setGrants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGrant, setExpandedGrant] = useState(null);
  const [updateDialog, setUpdateDialog] = useState({ open: false, grant: null, milestone: null });
  const [updateData, setUpdateData] = useState({ status: '', notes: '', proof_url: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [grantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/grants`),
        axios.get(`${API}/grants/stats/summary`)
      ]);
      setGrants(grantsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMilestoneUpdate = async () => {
    if (!updateData.status) {
      toast.error('Please select a status');
      return;
    }

    setIsUpdating(true);
    try {
      await axios.patch(
        `${API}/grants/${updateDialog.grant.id}/milestones/${updateDialog.milestone.id}`,
        {
          status: updateData.status,
          progress_notes: updateData.notes || null,
          proof_url: updateData.proof_url || null
        }
      );
      
      toast.success('Milestone updated successfully');
      setUpdateDialog({ open: false, grant: null, milestone: null });
      setUpdateData({ status: '', notes: '', proof_url: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update milestone';
      toast.error(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const getMilestoneIcon = (status) => {
    const icons = {
      pending: <Clock className="w-5 h-5 text-slate-400" />,
      in_progress: <AlertCircle className="w-5 h-5 text-yellow-400" />,
      completed: <CheckCircle className="w-5 h-5 text-blue-400" />,
      verified: <CheckCircle className="w-5 h-5 text-green-400" />
    };
    return icons[status] || icons.pending;
  };

  const getMilestoneColor = (status) => {
    const colors = {
      pending: 'border-slate-600 bg-slate-900/50',
      in_progress: 'border-yellow-500/50 bg-yellow-500/10',
      completed: 'border-blue-500/50 bg-blue-500/10',
      verified: 'border-green-500/50 bg-green-500/10'
    };
    return colors[status] || colors.pending;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-500/20 text-slate-400',
      in_progress: 'bg-yellow-500/20 text-yellow-400',
      completed: 'bg-blue-500/20 text-blue-400',
      verified: 'bg-green-500/20 text-green-400'
    };
    return styles[status] || styles.pending;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#02040A]" data-testid="grants-page">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="grants-headline">
              Grants Program
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Track grant disbursements and milestone progress for UNI2Chain ecosystem builders
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="glass-card rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(stats.total_budget)}
                        </div>
                        <div className="text-xs text-slate-400">Total Budget</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-card rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(stats.total_disbursed)}
                        </div>
                        <div className="text-xs text-slate-400">Disbursed</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-card rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{stats.active_grants}</div>
                        <div className="text-xs text-slate-400">Active Grants</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-card rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">
                          {stats.verified_milestones}/{stats.total_milestones}
                        </div>
                        <div className="text-xs text-slate-400">Milestones Verified</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Grants List */}
              {grants.length === 0 ? (
                <div className="glass-card rounded-xl p-12 text-center">
                  <DollarSign className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">No grants available yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {grants.map((grant) => {
                    const isExpanded = expandedGrant === grant.id;
                    const progressPercent = (grant.disbursed_amount / grant.total_budget) * 100;
                    
                    return (
                      <div 
                        key={grant.id} 
                        className="glass-card rounded-xl overflow-hidden"
                        data-testid={`grant-${grant.id}`}
                      >
                        {/* Grant Header */}
                        <div 
                          className="p-6 cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => setExpandedGrant(isExpanded ? null : grant.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-grow">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-white">{grant.project_name}</h3>
                                <Badge className={grant.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}>
                                  {grant.status}
                                </Badge>
                              </div>
                              <div className="text-sm text-slate-400">
                                Team: {grant.team_name}
                              </div>
                            </div>

                            <div className="flex items-center gap-6">
                              <div className="text-right">
                                <div className="text-xl font-bold text-white">
                                  {formatCurrency(grant.disbursed_amount)}
                                </div>
                                <div className="text-sm text-slate-400">
                                  of {formatCurrency(grant.total_budget)}
                                </div>
                              </div>
                              
                              <div className="w-32">
                                <Progress value={progressPercent} className="h-2" />
                                <div className="text-xs text-slate-400 text-right mt-1">
                                  {progressPercent.toFixed(0)}% disbursed
                                </div>
                              </div>
                              
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Milestones (Expanded) */}
                        {isExpanded && (
                          <div className="px-6 pb-6 border-t border-white/10">
                            <h4 className="text-sm font-medium text-slate-400 mt-4 mb-4">Milestones</h4>
                            
                            <div className="space-y-3">
                              {grant.milestones.map((milestone, idx) => (
                                <div 
                                  key={milestone.id}
                                  className={`rounded-lg border p-4 ${getMilestoneColor(milestone.status)}`}
                                >
                                  <div className="flex items-start gap-4">
                                    <div className="mt-1">
                                      {getMilestoneIcon(milestone.status)}
                                    </div>
                                    
                                    <div className="flex-grow">
                                      <div className="flex items-center gap-3 mb-1">
                                        <span className="font-medium text-white">
                                          {idx + 1}. {milestone.title}
                                        </span>
                                        <Badge className={getStatusBadge(milestone.status)}>
                                          {milestone.status.replace('_', ' ')}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-slate-400 mb-2">{milestone.description}</p>
                                      
                                      <div className="flex items-center gap-4 text-sm">
                                        <span className="flex items-center gap-1 text-slate-400">
                                          <DollarSign className="w-4 h-4" />
                                          {formatCurrency(milestone.amount)}
                                        </span>
                                        <span className="flex items-center gap-1 text-slate-400">
                                          <Calendar className="w-4 h-4" />
                                          Due: {formatDate(milestone.due_date)}
                                        </span>
                                      </div>
                                      
                                      {milestone.progress_notes && (
                                        <div className="mt-2 p-2 bg-white/5 rounded text-sm text-slate-300">
                                          <span className="text-slate-500">Notes:</span> {milestone.progress_notes}
                                        </div>
                                      )}
                                      
                                      {milestone.proof_url && (
                                        <a 
                                          href={milestone.proof_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                          View Proof
                                        </a>
                                      )}
                                    </div>
                                    
                                    <div>
                                      {milestone.status !== 'verified' && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setUpdateDialog({ open: true, grant, milestone });
                                            setUpdateData({ status: milestone.status, notes: milestone.progress_notes || '', proof_url: milestone.proof_url || '' });
                                          }}
                                          data-testid={`update-milestone-${milestone.id}`}
                                        >
                                          Update
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {grant.notes && (
                              <div className="mt-4 p-3 bg-white/5 rounded-lg">
                                <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
                                  <FileText className="w-4 h-4" />
                                  Grant Notes
                                </div>
                                <p className="text-sm text-slate-300">{grant.notes}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Update Milestone Dialog */}
      <Dialog open={updateDialog.open} onOpenChange={(open) => setUpdateDialog({ ...updateDialog, open })}>
        <DialogContent className="bg-[#0B0F19] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Update Milestone</DialogTitle>
            <DialogDescription className="text-slate-400">
              {updateDialog.milestone?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <div className="flex gap-2 flex-wrap">
                {['in_progress', 'completed'].map((status) => (
                  <Button
                    key={status}
                    type="button"
                    variant={updateData.status === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUpdateData(prev => ({ ...prev, status }))}
                  >
                    {status.replace('_', ' ')}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Note: Only admins can verify milestones</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Progress Notes</Label>
              <Textarea
                value={updateData.notes}
                onChange={(e) => setUpdateData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Describe your progress..."
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Proof URL (optional)</Label>
              <Input
                value={updateData.proof_url}
                onChange={(e) => setUpdateData(prev => ({ ...prev, proof_url: e.target.value }))}
                placeholder="https://..."
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <Button 
              onClick={handleMilestoneUpdate}
              className="w-full"
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Milestone'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
